import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Filter, MapPin, Home, Building2, HardHat, X, ChevronLeft, ChevronRight, Images, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Featured Projects Data
const FEATURED_PROJECTS = [
  {
    id: 'palmer',
    name: 'The Palmer Apartment Homes',
    location: 'Round Rock, Texas',
    serviceType: 'Post-Construction',
    headline: 'Large-Scale Post-Construction & Comprehensive Site Sanitation',
    description: 'ACH Cleaning Co. was entrusted with the meticulous restoration of The Palmer Apartment Homes, a premier residential community requiring turnkey readiness across an expansive 1,100-unit development. Our team executed comprehensive site sanitation protocols, delivering each residence to exacting standards befitting luxury modern living.',
    highlights: [
      { icon: 'number', value: '1,100', title: 'Luxury Residences', desc: 'Precision deep-cleaning of units ranging from 689 to 1,461 square feet, ensuring turnkey readiness' },
      { icon: 'home', title: 'Private Garages', desc: 'Meticulous detailing of all attached private garage facilities to pristine condition' },
      { icon: 'hardhat', title: 'Exterior & Common Areas', desc: 'Professional power-washing of all exterior walkways and revitalization of common area amenities' },
      { icon: 'check', title: 'On-Schedule & On-Budget', desc: 'Delivered with unwavering commitment to operational excellence, strictly within timeline and budget parameters' }
    ],
    gallery: [
      { url: 'https://customer-assets.emergentagent.com/job_fae10718-b07e-49a2-8e6b-86b138980c72/artifacts/i98e320e_image.png', caption: 'Resident Clubhouse & Entertainment Lounge' },
      { url: 'https://customer-assets.emergentagent.com/job_fae10718-b07e-49a2-8e6b-86b138980c72/artifacts/e6891dl9_image.png', caption: 'Open-Concept Living & Dining Space' },
      { url: 'https://customer-assets.emergentagent.com/job_fae10718-b07e-49a2-8e6b-86b138980c72/artifacts/y779j019_image.png', caption: 'Designer Bathroom with Modern Vanity' },
      { url: 'https://customer-assets.emergentagent.com/job_fae10718-b07e-49a2-8e6b-86b138980c72/artifacts/070ukfwu_image.png', caption: 'Gourmet Kitchen & Dining Area' }
    ]
  },
  {
    id: 'loren',
    name: 'The Loren Austin',
    location: 'Austin, Texas',
    serviceType: 'Post-Construction',
    headline: 'Luxury Hotel Post-Construction & Deep Cleaning',
    description: 'We had the privilege of completing the post-construction and deep-cleaning services for The Loren Austin, a prestigious luxury hotel. Our comprehensive scope of work ensured every detail met the highest standards of cleanliness and presentation.',
    highlights: [
      { icon: 'number', value: '108', title: 'Guest Rooms', desc: 'Complete deep cleaning of all luxury suites and guest accommodations' },
      { icon: 'building', title: 'Dining & Restaurant Areas', desc: 'Thorough cleaning of the main dining room and restaurant facilities' },
      { icon: 'hardhat', title: 'Walkways & Common Areas', desc: 'Power washing all walkways and meticulous cleaning of every corridor and hallway' }
    ],
    gallery: [
      { url: 'https://customer-assets.emergentagent.com/job_fae10718-b07e-49a2-8e6b-86b138980c72/artifacts/nu8mhnlh_image.png', caption: 'Luxury Guest Suite - Living Area' },
      { url: 'https://customer-assets.emergentagent.com/job_fae10718-b07e-49a2-8e6b-86b138980c72/artifacts/1rs67pzy_image.png', caption: 'Premium Bathroom with Marble Finishes' },
      { url: 'https://customer-assets.emergentagent.com/job_fae10718-b07e-49a2-8e6b-86b138980c72/artifacts/2u99bndo_image.png', caption: 'Elegant Bedroom Suite' },
      { url: 'https://customer-assets.emergentagent.com/job_fae10718-b07e-49a2-8e6b-86b138980c72/artifacts/4ub01x1a_image.png', caption: 'Corridor & Mini Bar Area' }
    ]
  }
];

// Lightbox Component
const Lightbox = ({ images, currentIndex, onClose, onNext, onPrev }) => {
  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') onNext();
      if (e.key === 'ArrowLeft') onPrev();
    };
    window.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [onClose, onNext, onPrev]);

  return (
    <div 
      className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
      onClick={onClose}
      data-testid="lightbox-overlay"
    >
      {/* Close Button */}
      <button 
        onClick={onClose}
        className="absolute top-4 right-4 z-50 p-2 text-white/70 hover:text-white transition-colors"
        data-testid="lightbox-close"
      >
        <X className="w-8 h-8" />
      </button>

      {/* Previous Button */}
      <button 
        onClick={(e) => { e.stopPropagation(); onPrev(); }}
        className="absolute left-4 md:left-8 z-50 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
        data-testid="lightbox-prev"
      >
        <ChevronLeft className="w-6 h-6" />
      </button>

      {/* Image Container */}
      <div 
        className="relative max-w-5xl max-h-[85vh] mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        <img 
          src={images[currentIndex].url}
          alt={images[currentIndex].caption}
          className="max-w-full max-h-[80vh] object-contain rounded-lg"
          data-testid="lightbox-image"
        />
        {/* Caption */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6 rounded-b-lg">
          <p className="font-body text-white text-center text-lg">
            {images[currentIndex].caption}
          </p>
          <p className="font-body text-white/60 text-center text-sm mt-1">
            {currentIndex + 1} / {images.length}
          </p>
        </div>
      </div>

      {/* Next Button */}
      <button 
        onClick={(e) => { e.stopPropagation(); onNext(); }}
        className="absolute right-4 md:right-8 z-50 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
        data-testid="lightbox-next"
      >
        <ChevronRight className="w-6 h-6" />
      </button>

      {/* Thumbnail Navigation */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
        {images.map((img, idx) => (
          <button
            key={idx}
            onClick={(e) => { e.stopPropagation(); }}
            className={`w-2 h-2 rounded-full transition-all ${
              idx === currentIndex ? 'bg-white w-6' : 'bg-white/40 hover:bg-white/60'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

const ProjectsPage = () => {
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  
  // Featured Projects Carousel state
  const [currentProjectIndex, setCurrentProjectIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  
  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Lightbox handlers
  const openLightbox = (images, startIndex = 0) => {
    setLightboxImages(images);
    setLightboxIndex(startIndex);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const nextImage = () => {
    setLightboxIndex((prev) => (prev + 1) % lightboxImages.length);
  };

  const prevImage = () => {
    setLightboxIndex((prev) => (prev - 1 + lightboxImages.length) % lightboxImages.length);
  };

  // Featured Projects Carousel handlers
  const nextProject = useCallback(() => {
    setCurrentProjectIndex((prev) => (prev + 1) % FEATURED_PROJECTS.length);
  }, []);

  const prevProject = useCallback(() => {
    setCurrentProjectIndex((prev) => (prev - 1 + FEATURED_PROJECTS.length) % FEATURED_PROJECTS.length);
  }, []);

  const goToProject = (index) => {
    setCurrentProjectIndex(index);
    setIsAutoPlaying(false);
    // Resume auto-play after 60 seconds of inactivity
    setTimeout(() => setIsAutoPlaying(true), 60000);
  };

  // Auto-rotate featured projects every 30 seconds
  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(() => {
      nextProject();
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [isAutoPlaying, nextProject]);

  // Pause auto-play when user interacts with arrows
  const handleManualNavigation = (direction) => {
    setIsAutoPlaying(false);
    if (direction === 'next') {
      nextProject();
    } else {
      prevProject();
    }
    // Resume auto-play after 60 seconds
    setTimeout(() => setIsAutoPlaying(true), 60000);
  };

  // Get current featured project
  const currentProject = FEATURED_PROJECTS[currentProjectIndex];

  // Render highlight icon
  const renderHighlightIcon = (highlight) => {
    if (highlight.icon === 'number') {
      return <span className="font-heading font-bold text-brand-navy text-xs">{highlight.value}</span>;
    }
    if (highlight.icon === 'home') return <Home className="w-4 h-4 text-brand-navy" />;
    if (highlight.icon === 'building') return <Building2 className="w-4 h-4 text-brand-navy" />;
    if (highlight.icon === 'hardhat') return <HardHat className="w-4 h-4 text-brand-navy" />;
    if (highlight.icon === 'check') return <CheckCircle className="w-4 h-4 text-green-600" />;
    return null;
  };

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        await axios.post(`${API}/seed`);
        const response = await axios.get(`${API}/projects`);
        setProjects(response.data);
      } catch (error) {
        console.error('Error fetching projects:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, []);

  const filteredProjects = filter === 'all' 
    ? projects 
    : projects.filter(p => p.service_type === filter);

  const getServiceIcon = (type) => {
    switch (type) {
      case 'residential':
        return Home;
      case 'commercial':
        return Building2;
      case 'construction':
        return HardHat;
      default:
        return Home;
    }
  };

  const filters = [
    { id: 'all', label: 'All Projects' },
    { id: 'residential', label: 'Residential' },
    { id: 'construction', label: 'Construction' }
  ];

  // Fallback projects if API hasn't returned data yet
  const fallbackProjects = [
    {
      id: 'project-1',
      title: 'Luxury Estate Deep Clean',
      location: 'West Lake Hills, TX',
      service_type: 'residential',
      description: 'Complete deep cleaning of a luxury estate including all bedrooms, bathrooms, kitchen, and outdoor living spaces.',
      image_url: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800'
    },
    {
      id: 'project-2',
      title: 'Hotel Renovation - 200 Rooms',
      location: 'Downtown Austin, TX',
      service_type: 'construction',
      description: 'Post-construction cleaning for a major hotel renovation project. All rooms cleaned and prepared for guest occupancy.',
      image_url: 'https://images.unsplash.com/photo-1590490360182-c33d57733427?w=800'
    },
    {
      id: 'project-3',
      title: 'New Home Construction',
      location: 'Steiner Ranch, TX',
      service_type: 'construction',
      description: 'Final cleaning for newly constructed luxury home. Removed all construction dust and debris.',
      image_url: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800'
    },
    {
      id: 'project-4',
      title: 'Apartment Complex - 150 Units',
      location: 'Pflugerville, TX',
      service_type: 'construction',
      description: 'Large-scale post-construction cleaning for new apartment development.',
      image_url: 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=800'
    },
    {
      id: 'project-5',
      title: 'Weekly Residential Service',
      location: 'Cedar Park, TX',
      service_type: 'residential',
      description: 'Ongoing weekly cleaning service for a family home with consistent quality maintenance.',
      image_url: 'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=800'
    },
    {
      id: 'project-6',
      title: 'Commercial Office Building',
      location: 'Georgetown, TX',
      service_type: 'construction',
      description: 'Post-renovation cleaning for 50,000 sq ft commercial office space.',
      image_url: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800'
    }
  ];

  const displayProjects = projects.length > 0 ? filteredProjects : 
    (filter === 'all' ? fallbackProjects : fallbackProjects.filter(p => p.service_type === filter));

  return (
    <div data-testid="projects-page">
      {/* Lightbox */}
      {lightboxOpen && (
        <Lightbox 
          images={lightboxImages}
          currentIndex={lightboxIndex}
          onClose={closeLightbox}
          onNext={nextImage}
          onPrev={prevImage}
        />
      )}

      {/* Hero Section */}
      <section className="relative py-20 md:py-28 bg-brand-surface" data-testid="projects-hero">
        <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
          <div className="max-w-3xl">
            <p className="text-brand-accent font-body text-sm font-semibold uppercase tracking-widest mb-4">
              Our Portfolio
            </p>
            <h1 className="font-heading font-bold text-4xl sm:text-5xl text-brand-navy tracking-tight leading-tight mb-6">
              Projects That<br />
              <span className="text-brand-slate">Speak for Themselves</span>
            </h1>
            <p className="font-body text-lg text-brand-charcoal leading-relaxed">
              From intimate residential spaces to massive commercial developments, explore our portfolio of successfully completed projects across the Greater Austin Area.
            </p>
          </div>
        </div>
      </section>

      {/* Featured Projects Carousel */}
      <section className="py-16 md:py-24 bg-white relative" data-testid="featured-projects-carousel">
        {/* Navigation Arrows - Outside Container */}
        <button
          onClick={() => handleManualNavigation('prev')}
          className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 z-10 p-3 bg-white shadow-lg hover:shadow-xl rounded-full text-brand-navy hover:text-brand-accent transition-all duration-300 hover:scale-110"
          data-testid="carousel-prev"
          aria-label="Previous project"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
        
        <button
          onClick={() => handleManualNavigation('next')}
          className="absolute right-2 md:right-6 top-1/2 -translate-y-1/2 z-10 p-3 bg-white shadow-lg hover:shadow-xl rounded-full text-brand-navy hover:text-brand-accent transition-all duration-300 hover:scale-110"
          data-testid="carousel-next"
          aria-label="Next project"
        >
          <ChevronRight className="w-6 h-6" />
        </button>

        <div className="max-w-7xl mx-auto px-5 md:px-12 lg:px-16">
          {/* Header with Dots */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-4">
            <div>
              <p className="text-brand-accent font-body text-sm font-semibold uppercase tracking-widest mb-2">
                Featured Project
              </p>
              <h2 
                className="font-heading font-bold text-3xl md:text-4xl text-brand-navy tracking-tight transition-all duration-500"
                key={currentProject.id}
              >
                {currentProject.name}
              </h2>
            </div>
            
            {/* Carousel Indicators */}
            <div className="flex items-center gap-3">
              {FEATURED_PROJECTS.map((project, idx) => (
                <button
                  key={project.id}
                  onClick={() => goToProject(idx)}
                  className={`transition-all duration-300 ${
                    idx === currentProjectIndex 
                      ? 'w-8 h-2 bg-brand-navy rounded-full' 
                      : 'w-2 h-2 bg-brand-slate/30 rounded-full hover:bg-brand-slate/50'
                  }`}
                  aria-label={`Go to ${project.name}`}
                  data-testid={`carousel-dot-${idx}`}
                />
              ))}
              <span className="ml-2 font-body text-xs text-brand-charcoal">
                {currentProjectIndex + 1} / {FEATURED_PROJECTS.length}
              </span>
            </div>
          </div>

          {/* Project Content - Animated */}
          <div 
            className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center transition-opacity duration-500"
            key={currentProject.id}
          >
            {/* Gallery Tile - Clickable */}
            <div 
              className="relative group cursor-pointer"
              onClick={() => openLightbox(currentProject.gallery, 0)}
              data-testid={`${currentProject.id}-gallery`}
            >
              {/* Main Image */}
              <div className="relative overflow-hidden rounded-2xl aspect-[4/3]">
                <img 
                  src={currentProject.gallery[0].url}
                  alt={`${currentProject.name} - Featured`}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-60 group-hover:opacity-80 transition-opacity" />
                
                {/* Gallery Badge */}
                <div className="absolute top-4 right-4 flex items-center gap-2 px-3 py-2 bg-white/90 backdrop-blur-sm rounded-full">
                  <Images className="w-4 h-4 text-brand-navy" />
                  <span className="font-body text-sm font-medium text-brand-navy">{currentProject.gallery.length} Photos</span>
                </div>

                {/* View Gallery Prompt */}
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="bg-white/95 backdrop-blur-sm px-6 py-3 rounded-full shadow-lg">
                    <span className="font-body font-medium text-brand-navy">Click to View Gallery</span>
                  </div>
                </div>

                {/* Service Type Badge */}
                <div className="absolute top-4 left-4">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-brand-navy text-white rounded-full font-body text-xs font-medium">
                    <HardHat className="w-3.5 h-3.5" />
                    {currentProject.serviceType}
                  </span>
                </div>
              </div>

              {/* Thumbnail Preview Row */}
              <div className="flex gap-2 mt-3">
                {currentProject.gallery.slice(1, 4).map((img, idx) => (
                  <div 
                    key={idx}
                    className="relative flex-1 aspect-[4/3] overflow-hidden rounded-lg opacity-80 group-hover:opacity-100 transition-opacity"
                  >
                    <img 
                      src={img.url}
                      alt={img.caption}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Project Details */}
            <div className="lg:pl-8">
              <div className="flex items-center gap-2 mb-4">
                <MapPin className="w-5 h-5 text-brand-accent" />
                <span className="font-body text-brand-charcoal">{currentProject.location}</span>
              </div>

              <h3 className="font-heading font-semibold text-xl text-brand-navy mb-4">
                {currentProject.headline}
              </h3>

              <p className="font-body text-brand-charcoal leading-relaxed mb-6">
                {currentProject.description}
              </p>

              <div className="space-y-4 mb-8">
                {currentProject.highlights.map((highlight, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-brand-surface rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      {renderHighlightIcon(highlight)}
                    </div>
                    <div>
                      <p className="font-body font-medium text-brand-navy">{highlight.title}</p>
                      <p className="font-body text-sm text-brand-charcoal">{highlight.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <Button 
                onClick={() => openLightbox(currentProject.gallery, 0)}
                className="bg-brand-navy hover:bg-brand-action text-white font-body font-medium rounded-full px-6 py-3"
                data-testid="view-gallery-btn"
              >
                <Images className="w-4 h-4 mr-2" />
                View Full Gallery
              </Button>
            </div>
          </div>

          {/* Auto-play indicator */}
          <div className="flex items-center justify-center mt-8 gap-2">
            <div className={`w-2 h-2 rounded-full ${isAutoPlaying ? 'bg-green-500 animate-pulse' : 'bg-slate-300'}`} />
            <span className="font-body text-xs text-brand-charcoal">
              {isAutoPlaying ? 'Auto-advancing in 30s' : 'Paused'}
            </span>
          </div>
        </div>
      </section>

      {/* Filter & Projects Grid */}
      <section className="section-padding bg-white" data-testid="projects-grid">
        <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3 mb-12">
            <Filter className="w-5 h-5 text-brand-charcoal" />
            <span className="font-body text-sm text-brand-charcoal mr-2">Filter by:</span>
            {filters.map((f) => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={`px-4 py-2 rounded-full font-body text-sm transition-colors ${
                  filter === f.id 
                    ? 'bg-brand-navy text-white' 
                    : 'bg-brand-surface text-brand-charcoal hover:bg-slate-200'
                }`}
                data-testid={`filter-${f.id}`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {/* Projects Grid */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="aspect-[4/3] rounded-2xl bg-slate-100 img-skeleton"></div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {displayProjects.map((project, index) => {
                const ServiceIcon = getServiceIcon(project.service_type);
                return (
                  <div 
                    key={project.id}
                    className="group cursor-pointer"
                    data-testid={`project-card-${index}`}
                  >
                    <div className="relative overflow-hidden rounded-2xl aspect-[4/3] mb-4">
                      <img 
                        src={project.image_url} 
                        alt={project.title}
                        className="w-full h-full object-cover img-zoom"
                      />
                      <div className="absolute inset-0 project-overlay opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6">
                        <p className="font-body text-sm text-white/90 line-clamp-3">
                          {project.description}
                        </p>
                      </div>
                      {/* Service type badge */}
                      <div className="absolute top-4 left-4">
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/90 backdrop-blur-sm rounded-full font-body text-xs font-medium text-brand-navy">
                          <ServiceIcon className="w-3.5 h-3.5" />
                          {project.service_type.charAt(0).toUpperCase() + project.service_type.slice(1)}
                        </span>
                      </div>
                    </div>
                    <div>
                      <h3 className="font-heading font-semibold text-lg text-brand-navy mb-1 group-hover:text-brand-action transition-colors">
                        {project.title}
                      </h3>
                      <div className="flex items-center gap-1.5 text-brand-charcoal">
                        <MapPin className="w-4 h-4" />
                        <span className="font-body text-sm">{project.location}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-brand-navy" data-testid="projects-stats">
        <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <p className="font-heading font-bold text-4xl text-white mb-1">500+</p>
              <p className="font-body text-sm text-slate-400">Residential Cleans</p>
            </div>
            <div>
              <p className="font-heading font-bold text-4xl text-white mb-1">50+</p>
              <p className="font-body text-sm text-slate-400">Construction Projects</p>
            </div>
            <div>
              <p className="font-heading font-bold text-4xl text-white mb-1">5000+</p>
              <p className="font-body text-sm text-slate-400">Hotel Rooms Cleaned</p>
            </div>
            <div>
              <p className="font-heading font-bold text-4xl text-white mb-1">25+</p>
              <p className="font-body text-sm text-slate-400">Years Experience</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-brand-surface" data-testid="projects-cta">
        <div className="max-w-4xl mx-auto px-5 md:px-8 lg:px-12 text-center">
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-brand-navy tracking-tight mb-4">
            Your Project Could Be Next
          </h2>
          <p className="font-body text-brand-charcoal text-lg mb-8">
            Whether it's a single home or a 200-room hotel, we're ready to deliver exceptional results.
          </p>
          <Link to="/#quote-form">
            <Button 
              className="bg-brand-navy hover:bg-brand-action text-white font-body font-medium rounded-full px-8 py-6 btn-shine"
              data-testid="projects-cta-btn"
            >
              Start Your Project
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ProjectsPage;
